import { useEffect, useState } from "react";

import { Box, Grid, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";

import AnnualStatistics from "../components/Charts/AnnualStatistics";
import BillingStatisticsChart from "../components/Charts/BillingStatisticsChart";
import ClientSourceChart from "../components/Charts/ClientSourceChart";
import GlobalStatistics from "../components/Charts/GlobalStatistics";
import RevenueByServiceTypeChart from "../components/Charts/RevenueByServiceTypeChart";
import SeasonalityHeatmap from "../components/Charts/SeasonalityHeatmap";
import ServiceTypeChart from "../components/Charts/ServiceTypeChart";
import ServiceTypeChartByYear from "../components/Charts/ServiceTypeChartByYear";
import SourceRevenueTopChart from "../components/Charts/SourceRevenueTopChart";
import YearlyBillingChart from "../components/Charts/YearlyBillingChart";
import { getAvailableYears } from "../components/utils";
import { useAuth } from "../contexts/AuthContext";

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    const fetchYears = async () => {
      if (currentUser) {
        const years = await getAvailableYears(currentUser.uid);
        setAvailableYears(years);
      }
    };
    fetchYears();
  }, [currentUser]);

  const handleYearChange = event => {
    setSelectedYear(event.target.value);
  };

  const panelSx = {
    p: { xs: 2, md: 2.5 },
    borderRadius: 3,
    border: "1px solid #e2e8f0",
    boxShadow: theme =>
      theme.palette.mode === "dark"
        ? "0 4px 6px rgba(0, 0, 0, 0.4)"
        : "0 1px 2px rgba(15, 23, 42, 0.06)",
    backgroundColor: theme => theme.palette.mode === "dark" ? "background.paper" : "#fff"
  };

  return (
    <Box sx={{ maxWidth: "100%", mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          background: theme =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(148, 163, 184, 0.14), rgba(71, 85, 105, 0.16))"
              : "#ffffff"
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 0.5,
            color: theme => theme.palette.mode === "dark" ? "primary.light" : "#0f172a",
            fontWeight: 800
          }}
        >
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Suivi des indicateurs et performance commerciale
        </Typography>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={panelSx}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 170 }}>
                Filtrer par année
              </Typography>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                fullWidth
                sx={{
                  maxWidth: { xs: "100%", md: 280 },
                  backgroundColor: "#fff",
                  borderRadius: 2
                }}
              >
                {availableYears.map(year => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <GlobalStatistics />
        </Grid>
        <Grid item xs={12}>
          <AnnualStatistics selectedYear={selectedYear} />
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Revenu et Statistiques {selectedYear}
            </Typography>
            <BillingStatisticsChart selectedYear={selectedYear} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Revenu et Statistiques par année
            </Typography>
            <YearlyBillingChart />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Source des clients
            </Typography>
            <ClientSourceChart />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Type de prestations (global)
            </Typography>
            <ServiceTypeChart />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Type de prestations pour {selectedYear}
            </Typography>
            <ServiceTypeChartByYear selectedYear={selectedYear} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Revenu par type de prestation
            </Typography>
            <RevenueByServiceTypeChart />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Saisonnalite ({selectedYear})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Heatmap des prestations planifiees par mois et jour de semaine
            </Typography>
            <SeasonalityHeatmap selectedYear={selectedYear} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Sources de leads - Top 5 CA ({selectedYear})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Chiffre d&apos;affaires confirme cumule par provenance client
            </Typography>
            <SourceRevenueTopChart selectedYear={selectedYear} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
